import { 
  Users, 
  BarChart3, 
  Target, 
  Lightbulb, 
  FileText, 
  Calendar,
  Shield,
  Zap,
  TrendingUp,
  Clock,
  Globe,
  Settings,
  Lock,
  Brain,
  CheckCircle,
  Activity,
  Rocket,
  Eye,
  Network,
  Database,
  AlertTriangle,
  Building,
  Leaf,
  Layers,
  Cog
} from 'lucide-react';
import { ProductConfigs } from './types';

export const consultingSolutionsConfigs: ProductConfigs = {
  'business-strategy-consulting': {
    keyBenefits: [
      {
        icon: Target,
        title: 'Strategic Planning & Roadmapping',
        description: 'Comprehensive strategic planning with market analysis, competitive intelligence, and strategic roadmap creation tools.',
        metrics: '85% improvement in strategic clarity',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: BarChart3,
        title: 'Market Analysis & Research Tools',
        description: 'Advanced market research platform with competitive intelligence, industry trends, and opportunity identification.',
        metrics: '90% accuracy in market predictions',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Users,
        title: 'Stakeholder Management & Communication',
        description: 'Comprehensive stakeholder engagement with communication planning, feedback collection, and alignment tracking.',
        metrics: '75% improvement in stakeholder alignment',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: CheckCircle,
        title: 'SWOT Analysis & Framework Tools',
        description: 'Structured analysis tools with SWOT framework, scenario planning, and strategic assessment capabilities.',
        metrics: '80% more accurate strategic assessments',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: TrendingUp,
        title: 'Performance Metrics & KPI Tracking',
        description: 'Advanced KPI monitoring with real-time dashboards, performance benchmarking, and progress tracking.',
        metrics: '95% KPI achievement rate',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Lightbulb,
        title: 'Business Model Canvas & Planning',
        description: 'Interactive business model development with canvas tools, validation frameworks, and iteration management.',
        metrics: '70% faster business model development',
        color: 'from-indigo-600 to-purple-700'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI-Powered Strategic Intelligence',
        description: 'Machine learning algorithms analyse market conditions, competitive landscape, and business data for strategic insights.',
        metrics: '90% accuracy in strategic recommendations',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Globe,
        title: 'Global Market Intelligence Engine',
        description: 'Real-time global market analysis with trend forecasting, competitive monitoring, and opportunity detection.',
        metrics: 'Monitor 1000+ global market indicators',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Zap,
        title: 'Strategic Scenario Simulation',
        description: 'Advanced scenario modelling with Monte Carlo simulations, risk assessment, and outcome prediction.',
        metrics: 'Process 500+ strategic scenarios',
        color: 'from-indigo-600 to-purple-700'
      },
      {
        icon: Activity,
        title: 'Real-Time Strategy Execution',
        description: 'Dynamic strategy execution with milestone tracking, automated alerts, and performance optimization.',
        metrics: '85% improvement in strategy execution',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Eye,
        title: 'Competitive Intelligence Dashboard',
        description: 'Advanced competitive monitoring with automated data collection, analysis, and strategic insights.',
        metrics: 'Track 200+ competitive indicators',
        color: 'from-purple-600 to-pink-700'
      },
      {
        icon: Rocket,
        title: 'Strategic Innovation Accelerator',
        description: 'Innovation framework with idea generation, evaluation tools, and strategic innovation pipeline management.',
        metrics: '3x faster innovation cycles',
        color: 'from-yellow-500 to-orange-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Comprehensive compliance management with SOC 2, ISO 27001, GDPR adherence and automated audit trails.',
        metrics: '100% regulatory compliance maintained',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: Activity,
        title: 'Security Performance Analytics',
        description: 'Advanced threat analysis with real-time monitoring, security posture optimization, and predictive threat modeling.',
        metrics: '99.9% threat detection accuracy',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Rocket,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade platform supporting unlimited users with auto-scaling security measures and global deployment.',
        metrics: 'Support 10,000+ concurrent users',
        color: 'from-blue-600 to-indigo-700'
      },
      {
        icon: CheckCircle,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat mitigation with intelligent firewall rules, DDoS protection, and automated security responses.',
        metrics: '99.99% uptime with zero breaches',
        color: 'from-red-600 to-rose-700'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, smart card support, and seamless identity management.',
        metrics: '100% secure access verification',
        color: 'from-purple-600 to-pink-700'
      },
      {
        icon: Eye,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, anomaly detection, and automated incident response protocols.',
        metrics: '<1ms security event processing',
        color: 'from-orange-600 to-red-700'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with secure backup, version control, and instant disaster recovery capabilities.',
        metrics: '99.999% data recovery guarantee',
        color: 'from-cyan-600 to-blue-700'
      },
      {
        icon: Network,
        title: 'Network Segmentation & Monitoring',
        description: 'Advanced micro-segmentation with network anomaly detection, traffic analysis, and automated threat isolation.',
        metrics: 'Zero lateral movement attacks',
        color: 'from-teal-600 to-cyan-700'
      },
      {
        icon: AlertTriangle,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing, compliance validation, and security optimization.',
        metrics: '24/7 continuous security monitoring',
        color: 'from-yellow-600 to-orange-700'
      }
    ]
  },

  'management-consulting-platform': {
    keyBenefits: [
      {
        icon: Users,
        title: 'Client Relationship Management',
        description: 'Comprehensive client management with engagement tracking, project coordination, and relationship nurturing for consulting excellence.',
        metrics: '85% improvement in client satisfaction',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Target,
        title: 'Strategic Project Management',
        description: 'Advanced project management with milestone tracking, resource allocation, and deliverable management for successful outcomes.',
        metrics: '70% faster project delivery',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: BarChart3,
        title: 'Business Intelligence Suite',
        description: 'Comprehensive analytics platform with client insights, project profitability analysis, and performance benchmarking.',
        metrics: '60% increase in project profitability',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Lightbulb,
        title: 'Innovation & Best Practice Library',
        description: 'Extensive methodology repository with consulting frameworks, best practices, and innovation tools for client success.',
        metrics: '90% methodology adoption rate',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Quality Assurance & Delivery',
        description: 'Comprehensive quality management with deliverable reviews, client feedback integration, and continuous improvement.',
        metrics: '95% client deliverable satisfaction',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Globe,
        title: 'Global Team Collaboration',
        description: 'Multi-regional consulting coordination with cultural adaptation, local expertise, and unified delivery standards.',
        metrics: 'Coordinate 50+ global consultants',
        color: 'from-indigo-600 to-purple-700'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'AI-Powered Insights Engine',
        description: 'Machine learning algorithms analyse market trends, client data, and industry patterns to provide strategic recommendations.',
        metrics: '90% accuracy in strategic predictions',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Brain,
        title: 'Knowledge Management System',
        description: 'Advanced knowledge repository with best practices, methodology libraries, and institutional memory preservation.',
        metrics: '80% faster knowledge retrieval',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: TrendingUp,
        title: 'Market Intelligence Platform',
        description: 'Real-time market analysis with competitive intelligence, trend forecasting, and opportunity identification capabilities.',
        metrics: 'Track 500+ market indicators',
        color: 'from-indigo-600 to-purple-700'
      },
      {
        icon: Activity,
        title: 'Automated Workflow Optimization',
        description: 'Intelligent process automation with consulting workflow optimization, resource allocation, and delivery acceleration.',
        metrics: '65% improvement in delivery efficiency',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Eye,
        title: 'Predictive Client Analytics',
        description: 'Advanced client behavior analysis with engagement prediction, satisfaction forecasting, and retention optimization.',
        metrics: 'Predict client needs with 85% accuracy',
        color: 'from-purple-600 to-pink-700'
      },
      {
        icon: Rocket,
        title: 'Scalable Consulting Platform',
        description: 'Enterprise-grade platform supporting unlimited consultants with global deployment and real-time collaboration.',
        metrics: 'Support 1000+ concurrent consultants',
        color: 'from-yellow-500 to-orange-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Comprehensive compliance management with SOC 2, ISO 27001, GDPR adherence and automated audit trails.',
        metrics: '100% regulatory compliance maintained',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: Activity,
        title: 'Security Performance Analytics',
        description: 'Advanced threat analysis with real-time monitoring, security posture optimization, and predictive threat modeling.',
        metrics: '99.9% threat detection accuracy',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Rocket,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade platform supporting unlimited users with auto-scaling security measures and global deployment.',
        metrics: 'Support 10,000+ concurrent users',
        color: 'from-blue-600 to-indigo-700'
      },
      {
        icon: CheckCircle,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat mitigation with intelligent firewall rules, DDoS protection, and automated security responses.',
        metrics: '99.99% uptime with zero breaches',
        color: 'from-red-600 to-rose-700'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, smart card support, and seamless identity management.',
        metrics: '100% secure access verification',
        color: 'from-purple-600 to-pink-700'
      },
      {
        icon: Eye,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, anomaly detection, and automated incident response protocols.',
        metrics: '<1ms security event processing',
        color: 'from-orange-600 to-red-700'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with secure backup, version control, and instant disaster recovery capabilities.',
        metrics: '99.999% data recovery guarantee',
        color: 'from-cyan-600 to-blue-700'
      },
      {
        icon: Network,
        title: 'Network Segmentation & Monitoring',
        description: 'Advanced micro-segmentation with network anomaly detection, traffic analysis, and automated threat isolation.',
        metrics: 'Zero lateral movement attacks',
        color: 'from-teal-600 to-cyan-700'
      },
      {
        icon: AlertTriangle,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing, compliance validation, and security optimization.',
        metrics: '24/7 continuous security monitoring',
        color: 'from-yellow-600 to-orange-700'
      }
    ]
  },

  'strategy-development-suite': {
    keyBenefits: [
      {
        icon: Lightbulb,
        title: 'Strategic Planning Framework',
        description: 'Comprehensive strategy development with scenario planning, SWOT analysis, and strategic roadmap creation tools.',
        metrics: '75% improvement in strategy clarity',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Target,
        title: 'Goal Setting & Tracking',
        description: 'Advanced goal management with OKR tracking, KPI monitoring, and automated progress reporting for strategic alignment.',
        metrics: '90% improvement in goal achievement',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: BarChart3,
        title: 'Performance Analytics Dashboard',
        description: 'Real-time strategy performance monitoring with predictive analytics, trend analysis, and actionable insights.',
        metrics: '85% accuracy in performance prediction',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Strategic Alignment & Communication',
        description: 'Comprehensive stakeholder alignment with communication tools, feedback systems, and consensus building.',
        metrics: '80% improvement in strategic alignment',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Risk Assessment & Mitigation',
        description: 'Advanced risk analysis with strategic risk identification, assessment frameworks, and mitigation planning tools.',
        metrics: '70% reduction in strategic risks',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Globe,
        title: 'Market Intelligence Integration',
        description: 'Real-time market data integration with competitive analysis, trend monitoring, and opportunity identification.',
        metrics: 'Monitor 800+ market variables',
        color: 'from-indigo-600 to-purple-700'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'AI-Driven Strategy Optimisation',
        description: 'Machine learning algorithms analyse market conditions, competitive landscape, and internal capabilities for optimal strategy.',
        metrics: '60% improvement in strategic outcomes',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Globe,
        title: 'Market Simulation Engine',
        description: 'Advanced market modelling with scenario testing, competitive response simulation, and risk assessment capabilities.',
        metrics: 'Simulate 1000+ market scenarios',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Settings,
        title: 'Strategic Workflow Automation',
        description: 'Automated strategy execution with milestone tracking, stakeholder notifications, and performance adjustment triggers.',
        metrics: '70% reduction in manual strategy tasks',
        color: 'from-indigo-600 to-purple-700'
      },
      {
        icon: Brain,
        title: 'Predictive Strategy Analytics',
        description: 'Advanced predictive modeling with strategy outcome forecasting, success probability analysis, and optimization recommendations.',
        metrics: '88% accuracy in strategy predictions',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Activity,
        title: 'Dynamic Strategy Adaptation',
        description: 'Real-time strategy adjustment with market change detection, adaptive planning, and automated strategy refinement.',
        metrics: '3x faster strategy adaptation',
        color: 'from-purple-600 to-pink-700'
      },
      {
        icon: Rocket,
        title: 'Strategic Innovation Platform',
        description: 'Innovation-focused strategy development with emerging technology assessment, disruption planning, and innovation roadmaps.',
        metrics: 'Identify 95% of strategic opportunities',
        color: 'from-yellow-500 to-orange-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Comprehensive compliance management with SOC 2, ISO 27001, GDPR adherence and automated audit trails.',
        metrics: '100% regulatory compliance maintained',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: Activity,
        title: 'Security Performance Analytics',
        description: 'Advanced threat analysis with real-time monitoring, security posture optimization, and predictive threat modeling.',
        metrics: '99.9% threat detection accuracy',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Rocket,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade platform supporting unlimited users with auto-scaling security measures and global deployment.',
        metrics: 'Support 10,000+ concurrent users',
        color: 'from-blue-600 to-indigo-700'
      },
      {
        icon: CheckCircle,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat mitigation with intelligent firewall rules, DDoS protection, and automated security responses.',
        metrics: '99.99% uptime with zero breaches',
        color: 'from-red-600 to-rose-700'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, smart card support, and seamless identity management.',
        metrics: '100% secure access verification',
        color: 'from-purple-600 to-pink-700'
      },
      {
        icon: Eye,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, anomaly detection, and automated incident response protocols.',
        metrics: '<1ms security event processing',
        color: 'from-orange-600 to-red-700'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with secure backup, version control, and instant disaster recovery capabilities.',
        metrics: '99.999% data recovery guarantee',
        color: 'from-cyan-600 to-blue-700'
      },
      {
        icon: Network,
        title: 'Network Segmentation & Monitoring',
        description: 'Advanced micro-segmentation with network anomaly detection, traffic analysis, and automated threat isolation.',
        metrics: 'Zero lateral movement attacks',
        color: 'from-teal-600 to-cyan-700'
      },
      {
        icon: AlertTriangle,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing, compliance validation, and security optimization.',
        metrics: '24/7 continuous security monitoring',
        color: 'from-yellow-600 to-orange-700'
      }
    ]
  },

  'business-transformation-platform': {
    keyBenefits: [
      {
        icon: TrendingUp,
        title: 'Change Management Excellence',
        description: 'Comprehensive change management with stakeholder engagement, communication planning, and transformation tracking.',
        metrics: '80% improvement in change adoption',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Users,
        title: 'Stakeholder Engagement Hub',
        description: 'Advanced stakeholder management with engagement tracking, feedback collection, and communication orchestration.',
        metrics: '90% stakeholder satisfaction rate',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: CheckCircle,
        title: 'Transformation Tracking',
        description: 'Real-time transformation monitoring with milestone tracking, benefit realisation, and ROI measurement capabilities.',
        metrics: '95% transformation success rate',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Target,
        title: 'Strategic Initiative Management',
        description: 'Comprehensive initiative planning with resource allocation, timeline management, and strategic alignment.',
        metrics: '85% on-time initiative delivery',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Performance & Impact Analytics',
        description: 'Advanced analytics with transformation impact measurement, performance benchmarking, and ROI analysis.',
        metrics: '75% average ROI improvement',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Lightbulb,
        title: 'Innovation & Culture Change',
        description: 'Cultural transformation tools with innovation management, behavioural change tracking, and organisational development.',
        metrics: '70% culture transformation success',
        color: 'from-indigo-600 to-purple-700'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'AI-Powered Change Analytics',
        description: 'Machine learning algorithms predict change resistance, optimise communication strategies, and enhance adoption rates.',
        metrics: '75% improvement in change prediction',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Brain,
        title: 'Transformation Methodology Library',
        description: 'Comprehensive methodology repository with best practices, templates, and proven transformation frameworks.',
        metrics: '500+ transformation methodologies',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Globe,
        title: 'Global Transformation Coordination',
        description: 'Multi-regional transformation management with cultural adaptation, local compliance, and unified reporting.',
        metrics: 'Coordinate 100+ global initiatives',
        color: 'from-indigo-600 to-purple-700'
      },
      {
        icon: Activity,
        title: 'Real-Time Transformation Intelligence',
        description: 'Live transformation monitoring with predictive analytics, risk detection, and automated course correction.',
        metrics: '90% early risk detection accuracy',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Eye,
        title: 'Organisational Readiness Assessment',
        description: 'Advanced readiness evaluation with capability mapping, change capacity analysis, and readiness optimization.',
        metrics: 'Assess 200+ readiness factors',
        color: 'from-purple-600 to-pink-700'
      },
      {
        icon: Rocket,
        title: 'Transformation Acceleration Engine',
        description: 'Speed optimization tools with bottleneck identification, process acceleration, and delivery optimization.',
        metrics: '50% faster transformation delivery',
        color: 'from-yellow-500 to-orange-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Comprehensive compliance management with SOC 2, ISO 27001, GDPR adherence and automated audit trails.',
        metrics: '100% regulatory compliance maintained',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: Activity,
        title: 'Security Performance Analytics',
        description: 'Advanced threat analysis with real-time monitoring, security posture optimization, and predictive threat modeling.',
        metrics: '99.9% threat detection accuracy',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Rocket,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade platform supporting unlimited users with auto-scaling security measures and global deployment.',
        metrics: 'Support 10,000+ concurrent users',
        color: 'from-blue-600 to-indigo-700'
      },
      {
        icon: CheckCircle,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat mitigation with intelligent firewall rules, DDoS protection, and automated security responses.',
        metrics: '99.99% uptime with zero breaches',
        color: 'from-red-600 to-rose-700'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, smart card support, and seamless identity management.',
        metrics: '100% secure access verification',
        color: 'from-purple-600 to-pink-700'
      },
      {
        icon: Eye,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, anomaly detection, and automated incident response protocols.',
        metrics: '<1ms security event processing',
        color: 'from-orange-600 to-red-700'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with secure backup, version control, and instant disaster recovery capabilities.',
        metrics: '99.999% data recovery guarantee',
        color: 'from-cyan-600 to-blue-700'
      },
      {
        icon: Network,
        title: 'Network Segmentation & Monitoring',
        description: 'Advanced micro-segmentation with network anomaly detection, traffic analysis, and automated threat isolation.',
        metrics: 'Zero lateral movement attacks',
        color: 'from-teal-600 to-cyan-700'
      },
      {
        icon: AlertTriangle,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing, compliance validation, and security optimization.',
        metrics: '24/7 continuous security monitoring',
        color: 'from-yellow-600 to-orange-700'
      }
    ]
  },

  'process-optimisation-suite': {
    keyBenefits: [
      {
        icon: Settings,
        title: 'Process Analysis & Mapping',
        description: 'Comprehensive process mapping with workflow analysis, bottleneck identification, and optimisation recommendations.',
        metrics: '65% improvement in process efficiency',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Performance Improvement Tracking',
        description: 'Advanced performance monitoring with KPI tracking, benchmarking, and continuous improvement management.',
        metrics: '50% reduction in process cycle time',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: BarChart3,
        title: 'ROI & Cost Analysis',
        description: 'Detailed cost-benefit analysis with ROI calculation, cost reduction tracking, and financial impact assessment.',
        metrics: '40% average cost reduction achieved',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: CheckCircle,
        title: 'Quality Assurance & Control',
        description: 'Comprehensive quality management with automated testing, compliance verification, and quality improvement tracking.',
        metrics: '99% quality assurance accuracy',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Users,
        title: 'Team Collaboration & Training',
        description: 'Advanced collaboration tools with team training, knowledge sharing, and skill development for process optimization.',
        metrics: '85% team efficiency improvement',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Globe,
        title: 'Standardization & Best Practices',
        description: 'Global process standardization with best practice libraries, template management, and compliance frameworks.',
        metrics: '90% process standardization achieved',
        color: 'from-indigo-600 to-purple-700'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'AI-Driven Process Intelligence',
        description: 'Machine learning algorithms analyse process patterns, predict bottlenecks, and recommend optimal workflows.',
        metrics: '85% accuracy in bottleneck prediction',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Globe,
        title: 'Process Standardisation Platform',
        description: 'Global process standardisation with best practice sharing, template libraries, and compliance management.',
        metrics: '90% process standardisation achieved',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: CheckCircle,
        title: 'Automated Quality Assurance',
        description: 'Intelligent quality monitoring with automated testing, compliance checking, and performance validation.',
        metrics: '99% quality assurance accuracy',
        color: 'from-indigo-600 to-purple-700'
      },
      {
        icon: Brain,
        title: 'Predictive Process Analytics',
        description: 'Advanced analytics with process outcome forecasting, optimization recommendations, and performance prediction.',
        metrics: '92% accuracy in process predictions',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Activity,
        title: 'Real-Time Process Monitoring',
        description: 'Live process tracking with instant alerts, performance dashboards, and automated optimization triggers.',
        metrics: '<5ms process monitoring updates',
        color: 'from-purple-600 to-pink-700'
      },
      {
        icon: Rocket,
        title: 'Process Automation Engine',
        description: 'Advanced automation with workflow optimization, task automation, and intelligent process orchestration.',
        metrics: '80% process automation coverage',
        color: 'from-yellow-500 to-orange-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Comprehensive compliance management with SOC 2, ISO 27001, GDPR adherence and automated audit trails.',
        metrics: '100% regulatory compliance maintained',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: Activity,
        title: 'Security Performance Analytics',
        description: 'Advanced threat analysis with real-time monitoring, security posture optimization, and predictive threat modeling.',
        metrics: '99.9% threat detection accuracy',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Rocket,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade platform supporting unlimited users with auto-scaling security measures and global deployment.',
        metrics: 'Support 10,000+ concurrent users',
        color: 'from-blue-600 to-indigo-700'
      },
      {
        icon: CheckCircle,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat mitigation with intelligent firewall rules, DDoS protection, and automated security responses.',
        metrics: '99.99% uptime with zero breaches',
        color: 'from-red-600 to-rose-700'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, smart card support, and seamless identity management.',
        metrics: '100% secure access verification',
        color: 'from-purple-600 to-pink-700'
      },
      {
        icon: Eye,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, anomaly detection, and automated incident response protocols.',
        metrics: '<1ms security event processing',
        color: 'from-orange-600 to-red-700'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with secure backup, version control, and instant disaster recovery capabilities.',
        metrics: '99.999% data recovery guarantee',
        color: 'from-cyan-600 to-blue-700'
      },
      {
        icon: Network,
        title: 'Network Segmentation & Monitoring',
        description: 'Advanced micro-segmentation with network anomaly detection, traffic analysis, and automated threat isolation.',
        metrics: 'Zero lateral movement attacks',
        color: 'from-teal-600 to-cyan-700'
      },
      {
        icon: AlertTriangle,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing, compliance validation, and security optimization.',
        metrics: '24/7 continuous security monitoring',
        color: 'from-yellow-600 to-orange-700'
      }
    ]
  },

  'financial-advisory-platform': {
    keyBenefits: [
      {
        icon: TrendingUp,
        title: 'Investment Analysis & Research',
        description: 'Advanced investment research platform with market analysis, risk assessment, and portfolio optimization tools.',
        metrics: '90% improvement in investment decision accuracy',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: BarChart3,
        title: 'Portfolio Management & Optimization',
        description: 'Comprehensive portfolio management with asset allocation, rebalancing automation, and performance tracking.',
        metrics: '85% increase in portfolio performance',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Shield,
        title: 'Risk Assessment & Management',
        description: 'Advanced risk analysis with stress testing, scenario modeling, and risk mitigation strategies.',
        metrics: '75% reduction in portfolio risk',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Target,
        title: 'Financial Planning & Forecasting',
        description: 'Comprehensive financial planning with goal setting, cash flow analysis, and retirement planning tools.',
        metrics: '95% client financial goal achievement',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Globe,
        title: 'Market Analysis & Insights',
        description: 'Real-time market intelligence with economic indicators, sector analysis, and investment opportunity identification.',
        metrics: 'Track 1000+ market indicators',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Users,
        title: 'Client Relationship Management',
        description: 'Advanced CRM with client onboarding, communication tracking, and relationship management tools.',
        metrics: '80% improvement in client satisfaction',
        color: 'from-indigo-600 to-purple-700'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI-Powered Investment Intelligence',
        description: 'Machine learning algorithms analyse market patterns, predict trends, and optimize investment strategies.',
        metrics: '92% accuracy in market predictions',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Automated Portfolio Rebalancing',
        description: 'Dynamic portfolio optimization with automated rebalancing, tax-loss harvesting, and risk management.',
        metrics: '70% improvement in portfolio efficiency',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Activity,
        title: 'Real-Time Risk Monitoring',
        description: 'Continuous risk assessment with real-time alerts, stress testing, and automated risk mitigation.',
        metrics: '<1ms risk calculation updates',
        color: 'from-indigo-600 to-purple-700'
      },
      {
        icon: Eye,
        title: 'Behavioral Finance Analytics',
        description: 'Advanced client behavior analysis with sentiment tracking, bias detection, and decision optimization.',
        metrics: 'Analyse 200+ behavioral indicators',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Rocket,
        title: 'Alternative Investment Platform',
        description: 'Comprehensive alternative investment analysis with private equity, hedge funds, and real estate evaluation.',
        metrics: 'Access 500+ alternative investments',
        color: 'from-purple-600 to-pink-700'
      },
      {
        icon: CheckCircle,
        title: 'Regulatory Compliance Automation',
        description: 'Automated compliance monitoring with regulatory reporting, audit trails, and fiduciary duty management.',
        metrics: '100% regulatory compliance maintained',
        color: 'from-yellow-500 to-orange-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Comprehensive compliance management with SOC 2, ISO 27001, GDPR adherence and automated audit trails.',
        metrics: '100% regulatory compliance maintained',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: Activity,
        title: 'Security Performance Analytics',
        description: 'Advanced threat analysis with real-time monitoring, security posture optimization, and predictive threat modeling.',
        metrics: '99.9% threat detection accuracy',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Rocket,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade platform supporting unlimited users with auto-scaling security measures and global deployment.',
        metrics: 'Support 10,000+ concurrent users',
        color: 'from-blue-600 to-indigo-700'
      },
      {
        icon: CheckCircle,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat mitigation with intelligent firewall rules, DDoS protection, and automated security responses.',
        metrics: '99.99% uptime with zero breaches',
        color: 'from-red-600 to-rose-700'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, smart card support, and seamless identity management.',
        metrics: '100% secure access verification',
        color: 'from-purple-600 to-pink-700'
      },
      {
        icon: Eye,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, anomaly detection, and automated incident response protocols.',
        metrics: '<1ms security event processing',
        color: 'from-orange-600 to-red-700'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with secure backup, version control, and instant disaster recovery capabilities.',
        metrics: '99.999% data recovery guarantee',
        color: 'from-cyan-600 to-blue-700'
      },
      {
        icon: Network,
        title: 'Network Segmentation & Monitoring',
        description: 'Advanced micro-segmentation with network anomaly detection, traffic analysis, and automated threat isolation.',
        metrics: 'Zero lateral movement attacks',
        color: 'from-teal-600 to-cyan-700'
      },
      {
        icon: AlertTriangle,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing, compliance validation, and security optimization.',
        metrics: '24/7 continuous security monitoring',
        color: 'from-yellow-600 to-orange-700'
      }
    ]
  },

  'digital-transformation-consulting': {
    keyBenefits: [
      {
        icon: Rocket,
        title: 'Technology Assessment & Strategy',
        description: 'Comprehensive technology evaluation with digital readiness assessment, technology stack optimization, and strategic planning.',
        metrics: '85% improvement in technology ROI',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Target,
        title: 'Digital Strategy Development',
        description: 'Advanced digital strategy creation with roadmap planning, capability assessment, and implementation guidance.',
        metrics: '90% digital strategy success rate',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Users,
        title: 'Change Management & Training',
        description: 'Comprehensive change management with employee training, adoption support, and cultural transformation guidance.',
        metrics: '80% user adoption improvement',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: BarChart3,
        title: 'Digital Performance Analytics',
        description: 'Advanced analytics with digital transformation tracking, ROI measurement, and performance optimization insights.',
        metrics: '75% faster performance insights',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Implementation Planning & Support',
        description: 'Detailed implementation planning with project management, risk mitigation, and delivery optimization.',
        metrics: '95% on-time implementation delivery',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Globe,
        title: 'Digital Culture Transformation',
        description: 'Cultural transformation with digital mindset development, innovation culture, and organisational change management.',
        metrics: '70% culture transformation success',
        color: 'from-indigo-600 to-purple-700'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI-Powered Transformation Intelligence',
        description: 'Machine learning algorithms analyse digital maturity, predict transformation outcomes, and optimize implementation strategies.',
        metrics: '88% accuracy in transformation predictions',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Activity,
        title: 'Digital Maturity Assessment Engine',
        description: 'Comprehensive digital maturity evaluation with capability mapping, gap analysis, and improvement roadmaps.',
        metrics: 'Assess 300+ digital capabilities',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Zap,
        title: 'Technology Integration Platform',
        description: 'Advanced integration planning with system compatibility analysis, integration roadmaps, and migration strategies.',
        metrics: '90% integration success rate',
        color: 'from-indigo-600 to-purple-700'
      },
      {
        icon: Eye,
        title: 'Real-Time Transformation Monitoring',
        description: 'Live transformation tracking with progress monitoring, risk detection, and automated course correction.',
        metrics: '24/7 transformation monitoring',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Settings,
        title: 'Cloud Migration Optimization',
        description: 'Intelligent cloud migration with workload analysis, cost optimization, and migration automation.',
        metrics: '60% cloud migration cost savings',
        color: 'from-purple-600 to-pink-700'
      },
      {
        icon: Lightbulb,
        title: 'Digital Skills Assessment',
        description: 'Comprehensive skills evaluation with training needs analysis, learning path creation, and competency tracking.',
        metrics: 'Evaluate 200+ digital skills',
        color: 'from-yellow-500 to-orange-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Comprehensive compliance management with SOC 2, ISO 27001, GDPR adherence and automated audit trails.',
        metrics: '100% regulatory compliance maintained',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: Activity,
        title: 'Security Performance Analytics',
        description: 'Advanced threat analysis with real-time monitoring, security posture optimization, and predictive threat modeling.',
        metrics: '99.9% threat detection accuracy',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Rocket,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade platform supporting unlimited users with auto-scaling security measures and global deployment.',
        metrics: 'Support 10,000+ concurrent users',
        color: 'from-blue-600 to-indigo-700'
      },
      {
        icon: CheckCircle,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat mitigation with intelligent firewall rules, DDoS protection, and automated security responses.',
        metrics: '99.99% uptime with zero breaches',
        color: 'from-red-600 to-rose-700'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, smart card support, and seamless identity management.',
        metrics: '100% secure access verification',
        color: 'from-purple-600 to-pink-700'
      },
      {
        icon: Eye,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, anomaly detection, and automated incident response protocols.',
        metrics: '<1ms security event processing',
        color: 'from-orange-600 to-red-700'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with secure backup, version control, and instant disaster recovery capabilities.',
        metrics: '99.999% data recovery guarantee',
        color: 'from-cyan-600 to-blue-700'
      },
      {
        icon: Network,
        title: 'Network Segmentation & Monitoring',
        description: 'Advanced micro-segmentation with network anomaly detection, traffic analysis, and automated threat isolation.',
        metrics: 'Zero lateral movement attacks',
        color: 'from-teal-600 to-cyan-700'
      },
      {
        icon: AlertTriangle,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing, compliance validation, and security optimization.',
        metrics: '24/7 continuous security monitoring',
        color: 'from-yellow-600 to-orange-700'
      }
    ]
  },

  'operational-excellence-platform': {
    keyBenefits: [
      {
        icon: Settings,
        title: 'Process Optimization & Lean Methodologies',
        description: 'Comprehensive process optimization with lean principles, waste reduction, and efficiency improvement strategies.',
        metrics: '70% improvement in operational efficiency',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Performance Tracking & KPI Management',
        description: 'Advanced performance monitoring with KPI dashboards, benchmarking, and continuous improvement tracking.',
        metrics: '85% KPI achievement improvement',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Target,
        title: 'Efficiency Improvement & Waste Reduction',
        description: 'Systematic efficiency enhancement with waste identification, elimination strategies, and productivity optimization.',
        metrics: '60% waste reduction achieved',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: BarChart3,
        title: 'Operational Analytics & Insights',
        description: 'Deep operational analytics with performance insights, trend analysis, and predictive optimization recommendations.',
        metrics: '90% accuracy in operational predictions',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: CheckCircle,
        title: 'Quality Management & Control',
        description: 'Comprehensive quality assurance with Six Sigma methodologies, quality control, and defect reduction.',
        metrics: '95% quality improvement rate',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Users,
        title: 'Team Excellence & Collaboration',
        description: 'Team optimization with collaboration tools, skill development, and operational excellence training.',
        metrics: '80% team productivity increase',
        color: 'from-indigo-600 to-purple-700'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI-Driven Process Intelligence',
        description: 'Machine learning algorithms analyse operational patterns, predict bottlenecks, and recommend process optimizations.',
        metrics: '92% accuracy in bottleneck prediction',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Activity,
        title: 'Real-Time Bottleneck Detection',
        description: 'Live operational monitoring with instant bottleneck identification, alert systems, and automated resolution.',
        metrics: '<30 seconds bottleneck detection',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Zap,
        title: 'Automated Workflow Optimization',
        description: 'Intelligent workflow automation with process streamlining, task optimization, and efficiency acceleration.',
        metrics: '75% workflow automation coverage',
        color: 'from-indigo-600 to-purple-700'
      },
      {
        icon: Eye,
        title: 'Predictive Efficiency Analytics',
        description: 'Advanced predictive modeling with efficiency forecasting, performance prediction, and optimization recommendations.',
        metrics: '88% efficiency prediction accuracy',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Cog,
        title: 'Lean Automation Tools',
        description: 'Comprehensive lean implementation with automation tools, waste elimination, and continuous improvement systems.',
        metrics: '65% manual task reduction',
        color: 'from-purple-600 to-pink-700'
      },
      {
        icon: Rocket,
        title: 'Performance Benchmarking Engine',
        description: 'Advanced benchmarking with industry comparisons, best practice identification, and performance optimization.',
        metrics: 'Benchmark against 1000+ organizations',
        color: 'from-yellow-500 to-orange-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Comprehensive compliance management with SOC 2, ISO 27001, GDPR adherence and automated audit trails.',
        metrics: '100% regulatory compliance maintained',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: Activity,
        title: 'Security Performance Analytics',
        description: 'Advanced threat analysis with real-time monitoring, security posture optimization, and predictive threat modeling.',
        metrics: '99.9% threat detection accuracy',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Rocket,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade platform supporting unlimited users with auto-scaling security measures and global deployment.',
        metrics: 'Support 10,000+ concurrent users',
        color: 'from-blue-600 to-indigo-700'
      },
      {
        icon: CheckCircle,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat mitigation with intelligent firewall rules, DDoS protection, and automated security responses.',
        metrics: '99.99% uptime with zero breaches',
        color: 'from-red-600 to-rose-700'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, smart card support, and seamless identity management.',
        metrics: '100% secure access verification',
        color: 'from-purple-600 to-pink-700'
      },
      {
        icon: Eye,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, anomaly detection, and automated incident response protocols.',
        metrics: '<1ms security event processing',
        color: 'from-orange-600 to-red-700'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with secure backup, version control, and instant disaster recovery capabilities.',
        metrics: '99.999% data recovery guarantee',
        color: 'from-cyan-600 to-blue-700'
      },
      {
        icon: Network,
        title: 'Network Segmentation & Monitoring',
        description: 'Advanced micro-segmentation with network anomaly detection, traffic analysis, and automated threat isolation.',
        metrics: 'Zero lateral movement attacks',
        color: 'from-teal-600 to-cyan-700'
      },
      {
        icon: AlertTriangle,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing, compliance validation, and security optimization.',
        metrics: '24/7 continuous security monitoring',
        color: 'from-yellow-600 to-orange-700'
      }
    ]
  },

  'innovation-consulting-platform': {
    keyBenefits: [
      {
        icon: Lightbulb,
        title: 'Ideation Tools & Creative Frameworks',
        description: 'Advanced ideation platform with creative thinking tools, brainstorming frameworks, and innovation methodologies.',
        metrics: '300% increase in viable ideas',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Rocket,
        title: 'Innovation Pipeline Management',
        description: 'Comprehensive innovation pipeline with idea tracking, development stages, and portfolio management.',
        metrics: '85% innovation project success rate',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: FileText,
        title: 'IP Strategy & Patent Management',
        description: 'Intellectual property strategy with patent analysis, IP portfolio management, and competitive intelligence.',
        metrics: '70% improvement in IP value',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Settings,
        title: 'R&D Management & Coordination',
        description: 'Research and development management with project coordination, resource allocation, and progress tracking.',
        metrics: '60% faster R&D cycles',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Innovation Metrics & ROI Analysis',
        description: 'Advanced innovation analytics with ROI measurement, success tracking, and performance benchmarking.',
        metrics: '90% innovation ROI visibility',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Users,
        title: 'Collaboration Platforms & Networks',
        description: 'Innovation collaboration with expert networks, cross-functional teams, and external partnership management.',
        metrics: '200+ innovation partners connected',
        color: 'from-indigo-600 to-purple-700'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI-Powered Idea Generation',
        description: 'Machine learning algorithms generate innovative concepts, analyse market opportunities, and predict innovation success.',
        metrics: '95% accuracy in innovation predictions',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Eye,
        title: 'Patent Analysis Engine',
        description: 'Advanced patent intelligence with landscape analysis, freedom-to-operate assessment, and competitive monitoring.',
        metrics: 'Analyse 10M+ patents globally',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Innovation Portfolio Optimization',
        description: 'Strategic portfolio management with risk-return optimization, resource allocation, and pipeline balancing.',
        metrics: '80% portfolio optimization improvement',
        color: 'from-indigo-600 to-purple-700'
      },
      {
        icon: TrendingUp,
        title: 'Trend Forecasting & Analysis',
        description: 'Predictive trend analysis with emerging technology identification, market trend forecasting, and opportunity mapping.',
        metrics: 'Track 500+ emerging trends',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Activity,
        title: 'Competitive Innovation Tracking',
        description: 'Real-time competitive intelligence with innovation monitoring, patent tracking, and strategic analysis.',
        metrics: 'Monitor 1000+ competitors',
        color: 'from-purple-600 to-pink-700'
      },
      {
        icon: CheckCircle,
        title: 'Innovation ROI Analytics',
        description: 'Comprehensive ROI analysis with value measurement, impact assessment, and investment optimization.',
        metrics: '75% innovation ROI improvement',
        color: 'from-yellow-500 to-orange-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Comprehensive compliance management with SOC 2, ISO 27001, GDPR adherence and automated audit trails.',
        metrics: '100% regulatory compliance maintained',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: Activity,
        title: 'Security Performance Analytics',
        description: 'Advanced threat analysis with real-time monitoring, security posture optimization, and predictive threat modeling.',
        metrics: '99.9% threat detection accuracy',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Rocket,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade platform supporting unlimited users with auto-scaling security measures and global deployment.',
        metrics: 'Support 10,000+ concurrent users',
        color: 'from-blue-600 to-indigo-700'
      },
      {
        icon: CheckCircle,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat mitigation with intelligent firewall rules, DDoS protection, and automated security responses.',
        metrics: '99.99% uptime with zero breaches',
        color: 'from-red-600 to-rose-700'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, smart card support, and seamless identity management.',
        metrics: '100% secure access verification',
        color: 'from-purple-600 to-pink-700'
      },
      {
        icon: Eye,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, anomaly detection, and automated incident response protocols.',
        metrics: '<1ms security event processing',
        color: 'from-orange-600 to-red-700'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with secure backup, version control, and instant disaster recovery capabilities.',
        metrics: '99.999% data recovery guarantee',
        color: 'from-cyan-600 to-blue-700'
      },
      {
        icon: Network,
        title: 'Network Segmentation & Monitoring',
        description: 'Advanced micro-segmentation with network anomaly detection, traffic analysis, and automated threat isolation.',
        metrics: 'Zero lateral movement attacks',
        color: 'from-teal-600 to-cyan-700'
      },
      {
        icon: AlertTriangle,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing, compliance validation, and security optimization.',
        metrics: '24/7 continuous security monitoring',
        color: 'from-yellow-600 to-orange-700'
      }
    ]
  },

  'sustainability-consulting-suite': {
    keyBenefits: [
      {
        icon: Leaf,
        title: 'ESG Assessment & Strategy',
        description: 'Comprehensive ESG evaluation with sustainability strategy development, materiality assessment, and reporting frameworks.',
        metrics: '90% ESG score improvement',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: BarChart3,
        title: 'Carbon Tracking & Management',
        description: 'Advanced carbon footprint tracking with emissions monitoring, reduction planning, and net-zero roadmaps.',
        metrics: '70% carbon footprint reduction',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Target,
        title: 'Sustainability Strategy Development',
        description: 'Strategic sustainability planning with goal setting, roadmap creation, and implementation guidance.',
        metrics: '85% sustainability goal achievement',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: CheckCircle,
        title: 'Regulatory Compliance & Reporting',
        description: 'Comprehensive compliance management with regulatory tracking, automated reporting, and audit preparation.',
        metrics: '100% regulatory compliance maintained',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: TrendingUp,
        title: 'Impact Measurement & Analytics',
        description: 'Advanced impact analytics with sustainability metrics, ROI measurement, and performance tracking.',
        metrics: '95% impact measurement accuracy',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Users,
        title: 'Stakeholder Reporting & Communication',
        description: 'Stakeholder engagement with transparent reporting, communication tools, and sustainability marketing.',
        metrics: '80% stakeholder engagement improvement',
        color: 'from-indigo-600 to-purple-700'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI-Powered Sustainability Analytics',
        description: 'Machine learning algorithms analyse sustainability data, predict environmental impact, and optimize green strategies.',
        metrics: '92% accuracy in sustainability predictions',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Activity,
        title: 'Carbon Footprint Optimization',
        description: 'Intelligent carbon management with automated tracking, optimization recommendations, and reduction strategies.',
        metrics: '75% carbon optimization efficiency',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Shield,
        title: 'ESG Risk Assessment Engine',
        description: 'Advanced ESG risk analysis with predictive modeling, scenario planning, and mitigation strategies.',
        metrics: 'Assess 300+ ESG risk factors',
        color: 'from-indigo-600 to-purple-700'
      },
      {
        icon: Zap,
        title: 'Sustainability Reporting Automation',
        description: 'Automated sustainability reporting with regulatory compliance, framework alignment, and stakeholder communication.',
        metrics: '90% reporting automation coverage',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Eye,
        title: 'Green Technology Evaluation',
        description: 'Comprehensive green technology assessment with ROI analysis, implementation planning, and impact measurement.',
        metrics: 'Evaluate 500+ green technologies',
        color: 'from-purple-600 to-pink-700'
      },
      {
        icon: Layers,
        title: 'Circular Economy Modeling',
        description: 'Advanced circular economy planning with waste reduction modeling, resource optimization, and sustainability design.',
        metrics: '80% waste reduction potential',
        color: 'from-yellow-500 to-orange-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Comprehensive compliance management with SOC 2, ISO 27001, GDPR adherence and automated audit trails.',
        metrics: '100% regulatory compliance maintained',
        color: 'from-slate-600 to-slate-800'
      },
      {
        icon: Activity,
        title: 'Security Performance Analytics',
        description: 'Advanced threat analysis with real-time monitoring, security posture optimization, and predictive threat modeling.',
        metrics: '99.9% threat detection accuracy',
        color: 'from-emerald-600 to-teal-700'
      },
      {
        icon: Rocket,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade platform supporting unlimited users with auto-scaling security measures and global deployment.',
        metrics: 'Support 10,000+ concurrent users',
        color: 'from-blue-600 to-indigo-700'
      },
      {
        icon: CheckCircle,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat mitigation with intelligent firewall rules, DDoS protection, and automated security responses.',
        metrics: '99.99% uptime with zero breaches',
        color: 'from-red-600 to-rose-700'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, smart card support, and seamless identity management.',
        metrics: '100% secure access verification',
        color: 'from-purple-600 to-pink-700'
      },
      {
        icon: Eye,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, anomaly detection, and automated incident response protocols.',
        metrics: '<1ms security event processing',
        color: 'from-orange-600 to-red-700'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification with secure backup, version control, and instant disaster recovery capabilities.',
        metrics: '99.999% data recovery guarantee',
        color: 'from-cyan-600 to-blue-700'
      },
      {
        icon: Network,
        title: 'Network Segmentation & Monitoring',
        description: 'Advanced micro-segmentation with network anomaly detection, traffic analysis, and automated threat isolation.',
        metrics: 'Zero lateral movement attacks',
        color: 'from-teal-600 to-cyan-700'
      },
      {
        icon: AlertTriangle,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning with penetration testing, compliance validation, and security optimization.',
        metrics: '24/7 continuous security monitoring',
        color: 'from-yellow-600 to-orange-700'
      }
    ]
  }
};